import { IGroup } from '@/interfaces/IGroup'
import { IPermissions } from '@/interfaces/IPermissions'
import { onboardingService } from '@/services/onboardingService'
import { notify } from '@kyvg/vue3-notification'
import { ActionTree, GetterTree, MutationTree } from 'vuex'
import { IEduvConsent } from './../../interfaces/IEduvConsent'
import { IOrganisatieUrlViewModel } from './../../interfaces/IOrganisatieUrl'
import { IPublisher } from './../../interfaces/IPublisher'
import { ISchool } from './../../interfaces/ISchool'
import { ISupplier } from './../../interfaces/ISupplier'
import { IUserLogin } from './../../interfaces/IUser'
import { settingsService } from './../../services/settingsService'

class SettingsState {
    school: ISchool = {} as ISchool
    groepen: IGroup[] = []
    leveranciers = {
        availableLeveranciers: [] as ISupplier[],
        chosenLeverancier: {} as ISupplier,
    }
    uitgevers: IPublisher[] = []
    eduvConsent: IEduvConsent[] = []
    lastLogins: IUserLogin[] = []
    pollingData = false
    regios: any[] = []
    verwerkingsOvereenkomstStatus: number | string | null = null
    verwerkingsOvereenkomstOk = false
    showVerwerkersBtn = false
    toestemming: IPermissions = { activatie: false, naam: 'Esis' }
    organisatieUrls: IOrganisatieUrlViewModel[] = []
    organisatieUrlsLoading = false
}

const getters: GetterTree<SettingsState, any> = {
    schoolInfo(store) {
        return () => store.school
    },
    groepenInSchool(store) {
        return () => store.groepen
    },
    leveranciers(store) {
        const leveranciers = store.leveranciers
        const sortedLeveranciers = leveranciers.availableLeveranciers.sort((a, b) => {
            const naamA = a.naam.toUpperCase()
            const naamB = b.naam.toUpperCase()
            if (naamA < naamB) {
                return -1
            }
            if (naamA > naamB) {
                return 1
            }
            return 0
        })
        return () => ({ ...store.leveranciers, availableLeveranciers: sortedLeveranciers })
    },
    uitgevers(store) {
        return () => store.uitgevers
    },
    eduvConsent(store) {
        return () => store.eduvConsent
    },
    lastLogins(store) {
        return () =>
            store.lastLogins
                .map((login: IUserLogin) => {
                    return {
                        ...login,
                        naam: [login.voornaam, login.tussenvoegsel, login.achternaam].filter(Boolean).join(' '),
                    }
                })
                .sort((a, b) => {
                    if (a.laatstIngelogdOp && b.laatstIngelogdOp) {
                        if (new Date(a.laatstIngelogdOp).getTime() > new Date(b.laatstIngelogdOp).getTime()) {
                            return -1
                        }
                        if (new Date(a.laatstIngelogdOp).getTime() < new Date(b.laatstIngelogdOp).getTime()) {
                            return 1
                        }
                    }
                    return 0
                })
    },
    pollingState(store) {
        return () => store.pollingData
    },
    schoolregios(store) {
        return () => store.regios
    },
    verwerkersovereenkomst(store) {
        return () => store.verwerkingsOvereenkomstStatus
    },
    verwerkersOvereenkomstOk(store) {
        return () => store.verwerkingsOvereenkomstOk
    },
    showVerwerkersBtn(store) {
        return () => store.showVerwerkersBtn
    },
    momentoAssistantIsEnabled(store) {
        const allowedBrins = import.meta.env.DEV ? ['80MM00'] : ['77MM00', '80CT00', '80NH00', '13SS00']
        return () => store.school?.brincode && allowedBrins.includes(store.school.brincode)
    },
    toestemming(store) {
        return () => store.toestemming
    },
    organisatieUrls(store) {
        return () => store.organisatieUrls
    },
    organisatieUrlsLoading(store) {
        return () => store.organisatieUrlsLoading
    },
}

const mutations: MutationTree<SettingsState> = {
    setSchool(state, payload) {
        state.school = payload
    },
    setGroepenInSchool(state, payload) {
        state.groepen = payload
    },
    setLeveranciers(state, payload) {
        state.leveranciers = payload
    },
    setUitgevers(state, payload) {
        state.uitgevers = payload
    },
    setEduvConsent(state, payload) {
        state.eduvConsent = payload
    },
    updateLeverancier(state, payload) {
        state.leveranciers.chosenLeverancier = payload
    },
    setLastlogins(state, payload) {
        state.lastLogins = payload
    },
    setPollingState(state, payload) {
        state.pollingData = payload
    },
    setVerwerkersOvereenkomst(state, payload) {
        state.verwerkingsOvereenkomstStatus = payload
    },
    setVerwerkersOvereenkomstOk(state, payload) {
        state.verwerkingsOvereenkomstOk = payload
    },
    setSchoolRegios(state, payload) {
        state.regios = payload
    },
    setVerwerkersOvereenkomstBtn(state, payload) {
        state.showVerwerkersBtn = payload
    },
    setToestemming(state, payload) {
        state.toestemming.activatie = payload
    },
    setOrganisatieUrls(state, payload: IOrganisatieUrlViewModel[]) {
        state.organisatieUrls = payload
    },
    setOrganisatieUrlsLoading(state, payload: boolean) {
        state.organisatieUrlsLoading = payload
    },
}

const actions: ActionTree<SettingsState, any> = {
    getSchool({ commit }) {
        return new Promise((resolve, reject) => {
            settingsService
                .getSchool()
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        commit('setSchool', data.data)
                        resolve(data.data)
                    }
                })
                .catch((e: Error) => {
                    notify({
                        group: 'requests',
                        title: 'Foutmelding',
                        type: 'error',
                        text: `Er ging iets mis met het ophalen van de schoolinformatie, Error: ${e.message}`,
                    })
                })
        })
    },
    getGroepenInSchool({ commit }) {
        return new Promise((resolve, reject) => {
            settingsService
                .getGroepenInSchool()
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        commit('setGroepenInSchool', data.data)
                        resolve(data.data)
                    }
                })
                .catch((e: Error) => {
                    notify({
                        group: 'requests',
                        title: 'Foutmelding',
                        type: 'error',
                        text: `Er ging iets mis met het ophalen van de groepen in de school, Error: ${e.message}`,
                    })
                })
        })
    },
    getLeveranciers({ commit }) {
        return new Promise((resolve, reject) => {
            settingsService
                .getLeveranciers()
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        commit('setLeveranciers', data.data)
                        resolve(data.data)
                    }
                })
                .catch((e: Error) => {
                    notify({
                        group: 'requests',
                        title: 'Foutmelding',
                        type: 'error',
                        text: `Er ging iets mis met het ophalen van de leveranciers, Error: ${e.message}`,
                    })
                })
        })
    },
    getUitgevers({ commit }) {
        return new Promise((resolve, reject) => {
            settingsService
                .getUitgevers()
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        commit('setUitgevers', data.data)
                        resolve(data.data)
                    }
                })
                .catch((e: Error) => {
                    notify({
                        group: 'requests',
                        title: 'Foutmelding',
                        type: 'error',
                        text: `Er ging iets mis met het ophalen van de uitgevers, Error: ${e.message}`,
                    })
                })
        })
    },
    getVerwerkersOvereenkomst({ commit }) {
        return new Promise((resolve, reject) => {
            settingsService
                .getVerwerkersOvereenkomst()
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        commit('setVerwerkersOvereenkomst', data.data)
                        const isOk = data.data === 9
                        const showBtn = data.data === 0 || data.data === 99
                        commit('setVerwerkersOvereenkomstOk', isOk)
                        commit('setVerwerkersOvereenkomstBtn', showBtn)
                        resolve(data.data)
                    }
                })
                .catch((e: Error) => {
                    commit('setVerwerkersOvereenkomst', `Fout bij ophalen status. \n (Error: ${e.message})`)
                    commit('setVerwerkersOvereenkomstOk', false)
                    reject(e)
                })
        })
    },
    getSchoolRegios({ commit }) {
        return new Promise((resolve, reject) => {
            onboardingService
                .getRegios()
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        commit('setSchoolRegios', data.data)
                        resolve(data.data)
                    }
                })
                .catch((e) => {
                    reject(e)
                })
        })
    },
    getLastLogins({ commit }) {
        return new Promise((resolve, reject) => {
            settingsService
                .getlastLogins()
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        commit('setLastlogins', data.data)
                        resolve(data.data)
                    }
                })
                .catch((e: Error) => {
                    notify({
                        group: 'requests',
                        title: 'Foutmelding',
                        type: 'error',
                        text: `Er ging iets mis met het ophalen van de laatste logins, Error: ${e.message}`,
                    })
                })
        })
    },
    setLeverancier({ state }) {
        return new Promise((resolve, reject) => {
            settingsService
                .setLeverancier(state.leveranciers.chosenLeverancier.id)
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        resolve(data.data)
                        notify({
                            group: 'requests',
                            title: 'Gelukt',
                            type: 'success',
                            text: `Leverancier gewijzigd!`,
                        })
                    }
                })
                .catch((e: Error) => {
                    notify({
                        group: 'requests',
                        title: 'Foutmelding',
                        type: 'error',
                        text: `Er ging iets mis met het updaten van de leverancier, Error: ${e.message}`,
                    })
                })
        })
    },
    getEduvConsent({ commit }) {
        return new Promise((resolve, reject) => {
            settingsService
                .getEduvConsent()
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        commit('setEduvConsent', data.data)
                        resolve(data.data)
                    }
                })
                .catch((e: Error) => {
                    notify({
                        group: 'requests',
                        title: 'Foutmelding',
                        type: 'error',
                        text: `Er ging iets mis met het ophalen van de gegevensuitwisseling toestemmingen, Error: ${e.message}`,
                    })
                })
        })
    },
    setEduvConsent({ dispatch }, payload) {
        return new Promise((resolve, reject) => {
            settingsService
                .setEduvConsent(payload)
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        resolve(data.data)
                        notify({
                            group: 'requests',
                            title: 'Gelukt',
                            type: 'success',
                            text: `Toestemming voor gegevensuitwisseling ${payload.toestemming ? 'verleend' : 'ingetrokken'}!`,
                        })
                        dispatch('getEduvConsent')
                    }
                })
                .catch((e: Error) => {
                    notify({
                        group: 'requests',
                        title: 'Foutmelding',
                        type: 'error',
                        text: `Er ging iets mis met het updaten van de toestemming, Error: ${e.message}`,
                    })
                })
        })
    },
    getToestemming({ commit }) {
        return new Promise((resolve, reject) => {
            settingsService
                .getEsisToestemming()
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        commit('setToestemming', data.data)
                        resolve(data.data)
                    }
                })
                .catch((e: Error) => {
                    notify({
                        group: 'requests',
                        title: 'Foutmelding',
                        type: 'error',
                        text: `Er ging iets mis met het ophalen van de third party toestemming, Error: ${e.message}`,
                    })
                })
        })
    },
    setToestemming({ state }) {
        return new Promise((resolve, reject) => {
            settingsService
                .setEsisToestemming(state.toestemming.activatie)
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        resolve(data.data)
                        notify({
                            group: 'requests',
                            title: 'Gelukt',
                            type: 'success',
                            text: `Toestemming gewijzigd!`,
                        })
                    }
                })
                .catch((e: Error) => {
                    notify({
                        group: 'requests',
                        title: 'Foutmelding',
                        type: 'error',
                        text: `Er ging iets mis met het updaten van de toestemming, Error: ${e.message}`,
                    })
                })
        })
    },
    getOrganisatieUrls({ commit }, organisatieId?: number) {
        commit('setOrganisatieUrlsLoading', true)
        return new Promise((resolve, reject) => {
            settingsService
                .getOrganisatieUrls(organisatieId)
                .then((data) => {
                    if (data.status == 401) {
                        reject('unauthorized')
                    } else {
                        commit('setOrganisatieUrls', data.data)
                        resolve(data.data)
                    }
                })
                .catch((e: Error) => {
                    notify({
                        group: 'requests',
                        title: 'Foutmelding',
                        type: 'error',
                        text: `Er ging iets mis met het ophalen van de organisatie URLs, Error: ${e.message}`,
                    })
                    reject(e)
                })
                .finally(() => {
                    commit('setOrganisatieUrlsLoading', false)
                })
        })
    },
}

export default {
    state: new SettingsState(),
    getters: getters,
    mutations: mutations,
    actions: actions,
}
